import React, { useEffect, useState, useCallback } from 'react';
import api from '../../api/client';
import { useToast } from '../../context/ToastContext';

const STATUS_TABS = [
  { key: '', label: '전체' },
  { key: 'PENDING', label: 'PENDING' },
  { key: 'CONFIRMED', label: 'CONFIRMED' },
  { key: 'UNSUBSCRIBED', label: 'UNSUBSCRIBED' },
];

const STATUS_CHIP = {
  PENDING:      { cls: 'chip-pending',   text: 'PENDING' },
  CONFIRMED:    { cls: 'chip-active',    text: 'CONFIRMED' },
  UNSUBSCRIBED: { cls: 'chip-inactive',  text: 'UNSUBSCRIBED' },
};

function fmt(dt) {
  if (!dt) return '-';
  return new Date(dt).toLocaleDateString('ko-KR', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function SubscriberManage() {
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage]                 = useState(0);
  const [data, setData]                 = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading]           = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page };
    if (statusFilter) params.status = statusFilter;
    api.get('/api/admin/subscribers', { params })
      .then((res) => setData(res.data))
      .catch(() => toast('구독자 목록 조회 실패', 'error'))
      .finally(() => setLoading(false));
  }, [statusFilter, page, toast]);

  // PENDING 건수 배지용 별도 조회
  const loadPendingCount = useCallback(() => {
    api.get('/api/admin/subscribers', { params: { status: 'PENDING', page: 0 } })
      .then((res) => setPendingCount(res.data.totalElements ?? 0))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); loadPendingCount(); }, [load, loadPendingCount]);

  const handleTabChange = (key) => {
    setStatusFilter(key);
    setPage(0);
  };

  const handleResend = async (id, email) => {
    try {
      await api.post(`/api/admin/subscribers/${id}/resend-confirm`);
      toast(`${email} 에 확인 메일을 재발송했습니다.`, 'success');
    } catch (err) {
      toast(err.response?.data?.message || '재발송 실패', 'error');
    }
  };

  const handleConfirm = async (id, email) => {
    if (!window.confirm(`${email} 을 수동으로 구독 확인 처리하시겠습니까?`)) return;
    try {
      await api.post(`/api/admin/subscribers/${id}/confirm`);
      toast('구독 확인 처리되었습니다.', 'success');
      load(); loadPendingCount();
    } catch (err) {
      toast(err.response?.data?.message || '처리 실패', 'error');
    }
  };

  const handleDelete = async (id, email) => {
    if (!window.confirm(`${email} 구독자를 삭제하시겠습니까?`)) return;
    try {
      await api.delete(`/api/admin/subscribers/${id}`);
      toast('구독자가 삭제되었습니다.', 'info');
      load(); loadPendingCount();
    } catch (err) {
      toast(err.response?.data?.message || '삭제 실패', 'error');
    }
  };

  const subscribers = data?.content ?? [];
  const totalPages  = data?.totalPages ?? 0;
  const totalElements = data?.totalElements ?? 0;

  return (
    <div className="subscriber-manage">
      <div className="subscriber-header">
        <h2>구독자 관리</h2>
        <span className="subscriber-total">전체 {totalElements}명</span>
      </div>

      {/* 상태 필터 탭 */}
      <div className="subscriber-tabs">
        {STATUS_TABS.map(({ key, label }) => (
          <button
            key={key}
            className={`subscriber-tab${statusFilter === key ? ' active' : ''}`}
            onClick={() => handleTabChange(key)}
          >
            {label}
            {key === 'PENDING' && pendingCount > 0 && (
              <span className="pending-badge">{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* 구독자 테이블 */}
      {loading ? (
        <p className="subscriber-loading">조회 중...</p>
      ) : subscribers.length === 0 ? (
        <p className="subscriber-empty">해당하는 구독자가 없습니다.</p>
      ) : (
        <div className="subscriber-table-wrap">
          <table className="subscriber-table">
            <thead>
              <tr>
                <th>이메일</th>
                <th>상태</th>
                <th>신청일</th>
                <th>확인일</th>
                <th>액션</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map((s) => {
                const chip = STATUS_CHIP[s.status] ?? { cls: '', text: s.status };
                return (
                  <tr key={s.id}>
                    <td className="subscriber-email">{s.email}</td>
                    <td>
                      <span className={`status-chip ${chip.cls}`}>{chip.text}</span>
                    </td>
                    <td className="subscriber-date">{fmt(s.createdAt)}</td>
                    <td className="subscriber-date">{fmt(s.confirmedAt)}</td>
                    <td className="subscriber-actions">
                      {s.status === 'PENDING' && (
                        <>
                          <button
                            className="btn-sm"
                            onClick={() => handleResend(s.id, s.email)}
                          >
                            재발송
                          </button>
                          <button
                            className="btn-sm btn-success"
                            onClick={() => handleConfirm(s.id, s.email)}
                          >
                            수동확인
                          </button>
                        </>
                      )}
                      <button
                        className="btn-sm btn-danger"
                        onClick={() => handleDelete(s.id, s.email)}
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="subscriber-pagination">
          <button disabled={page === 0} onClick={() => setPage(page - 1)}>이전</button>
          <span>{page + 1} / {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>다음</button>
        </div>
      )}
    </div>
  );
}
